import { useEffect } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import { get } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { BoxV2, MainTabsV2, PlanType } from '@labrav/react-components'
import { useTheme } from '@mui/material'
import {
  isNonEmptyNumber,
  scrollspyTabs,
  useGetAllUsers,
  useUserType,
} from '@labrav/common'

import { useStyles } from './AddEditRenewChannelOfferForm.styles'
import {
  getIsErrorForChannelOfferTab,
  getMarkupValues,
} from '../../../../utils/channelOfferUtil'
import type { ChannelPartnerPrivateOfferInCreation } from '../../../../types/flyout'
import { DiscountType } from '../../../../types/flyout'
import type { ResaleAuthorizationsResellerAgreements } from '../../../../types/resaleAuthorization'
import { RenewChannelOfferContractDuration } from '../RenewChannelOfferContractDuration/RenewChannelOfferContractDuration'
import type { RenewChannelOfferFormProps } from '../renewChannelOfferTypes'
import { PrivateOfferStakeholders } from '../../../PrivateOfferFormV2/AwsOffer/PrivateOfferStakeholders/PrivateOfferStakeholders'
import { PrivateOfferCustomMetaData } from '../../../PrivateOfferFormV2/AwsOffer/PrivateOfferCustomeMetaData/PrivateOfferCustomMetaData'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from '../../../PrivateOfferFormV2/AzureOffer/MicrosoftFormFAQs/MicrosoftFormFAQs'
import { ChannelOfferDetail } from '../../../ChannelOffers/AddEditChannelOfferComponents/ChannelOfferDetail/ChannelOfferDetail'
import { OfferMarkup } from '../../../ChannelOffers/AddEditChannelOfferComponents/OfferMarkup/OfferMarkup'
import { ProductDimensions } from '../../../ChannelOffers/AddEditChannelOfferComponents/ProductDimensions/ProductDimensions'
import { AdditionalUsageAndSubscriptionDimensions } from '../../../ChannelOffers/AddEditChannelOfferComponents/AdditionalUsageAndSubscriptionDimensions/AdditionalUsageAndSubscriptionDimensions'
import { InstallmentsInfo } from '../../../ChannelOffers/AddEditChannelOfferComponents/InstallmentsInfo/InstallmentsInfo'
import { ChannelOfferEula } from '../../../ChannelOffers/AddEditChannelOfferComponents/ChannelOfferEula/ChannelOfferEula'
import { ResellerAgreementAcceptance } from '../../../ChannelOffers/AddEditChannelOfferComponents/ResellerAgreementAcceptance/ResellerAgreementAcceptance'

export const AddEditRenewChannelOfferForm = ({
  resellerAuthorization,
  subscriptionDetail,
}: RenewChannelOfferFormProps) => {
  const partnerType = useUserType()
  const { users } = useGetAllUsers(partnerType)
  const theme = useTheme()
  const offset = 240
  const { classes } = useStyles({ offset })

  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const { values, setFieldValue } = formikProps
  const additionalUsageDimensions = (values?.dimensions || []).filter(
    item => item?.isAdditionalUsageAllowed || item?.type === 'additional_usage'
  )
  const isAdditionalUsageDimensions = !!additionalUsageDimensions.length

  const { totalDueToIsv } = getMarkupValues({
    dimensions: values?.dimensions,
    adjustmentPercentage: values?.adjustmentPercentage,
    priceAdjustmentType: values?.priceAdjustmentType,
    resaleAuthorization: resellerAuthorization as
      | ResaleAuthorizationsResellerAgreements
      | undefined,
  })

  useEffect(() => {
    const installmentsArray = values?.installments || []
    if (installmentsArray.length) {
      const updateInstallments = async () => {
        const installments = values?.installments?.map(item => ({
          ...item,
          otherAmount: isNonEmptyNumber(totalDueToIsv)
            ? parseFloat((totalDueToIsv! / installmentsArray.length).toFixed(2))
            : '-',
        }))
        await setFieldValue('installments', installments)
      }
      updateInstallments()
    }
  }, [totalDueToIsv, setFieldValue, values?.installments])

  const channelOfferTabs = [
    {
      key: 'channel-offer-details',
      label: 'Offer details',
      renderElement: (
        <div
          id={'channel-offer-details'}
          data-testid={'channel-offer-details-section'}
          className={classes.scrollTopMargin}
        >
          <ChannelOfferDetail
            productType={
              (resellerAuthorization?.productType as PlanType) ||
              PlanType.SAAS_CONTRACT
            }
            buyerAwsAccountId={resellerAuthorization?.buyerAwsAccountId}
            isAbo
          />
        </div>
      ),
      isError: getIsErrorForChannelOfferTab(
        'channel-offer-details',
        formikProps,
        (resellerAuthorization?.productType as PlanType | undefined) ||
          PlanType.SAAS_CONTRACT
      ),
    },
    {
      key: 'channel-offer-pricing',
      label: 'Pricing',
      renderElement: (
        <div
          id={'channel-offer-pricing'}
          data-testid={'channel-offer-pricing-section'}
          className={classes.scrollTopMargin}
        >
          <RenewChannelOfferContractDuration
            resellerAuthorization={resellerAuthorization}
          />
          <OfferMarkup
            productType={
              (resellerAuthorization?.productType as PlanType) ||
              PlanType.SAAS_CONTRACT
            }
            resaleAuthorization={resellerAuthorization as never}
            discountType={resellerAuthorization?.discountType as DiscountType}
            shouldShowMultiple={
              resellerAuthorization?.discountType ===
              DiscountType.FLEXIBLE_PAYMENT_TERMS
            }
            showTcv
            isUpdateIsvPayment
          />
          <ProductDimensions
            productType={
              (resellerAuthorization?.productType as PlanType) ||
              PlanType.SAAS_CONTRACT
            }
            resellerAuthorization={resellerAuthorization as never}
          />

          {isAdditionalUsageDimensions && (
            <BoxV2
              heading={'Usage dimensions'}
              headingIcon={
                <FontAwesomeIcon
                  icon={faShelves}
                  style={{ width: '18px', height: '14px' }}
                />
              }
            >
              <div className={classes.rowWrapper}>
                <div className={classes.tableSubHeading}>
                  The unit price for each usage dimension is determined by
                  applying the same markup ratio.
                </div>
                <AdditionalUsageAndSubscriptionDimensions
                  dimensions={additionalUsageDimensions?.map(item => {
                    const resaleDimension = (
                      resellerAuthorization?.dimensions || []
                    )?.find(
                      resaleDimension =>
                        resaleDimension?.name === item?.name &&
                        resaleDimension?.isAdditionalUsageAllowed
                    )
                    const wholesalePrice = get(
                      resaleDimension?.prices,
                      'additionalUsagePrice',
                      0
                    )
                    const buyerPrice = get(
                      item?.prices,
                      'additionalUsagePrice',
                      0
                    )

                    return {
                      ...item,
                      wholesalePrice,
                      margin: buyerPrice ? buyerPrice - wholesalePrice : '',
                    }
                  })}
                  priceArray={[
                    {
                      keyName: 'wholesalePrice',
                      label: 'Wholesale unit price',
                    },
                    {
                      keyName: 'prices.additionalUsagePrice',
                      label: 'Buyer unit price',
                    },
                    {
                      keyName: 'margin',
                      label: 'Margin',
                    },
                  ]}
                />
              </div>
            </BoxV2>
          )}
          <InstallmentsInfo
            discountType={resellerAuthorization?.discountType as DiscountType}
            resaleAuthorization={resellerAuthorization as never}
            isAbo
            remainingInstallementAmount={
              subscriptionDetail?.subscription?.remainingInstallementAmount
            }
          />
        </div>
      ),
      isError: getIsErrorForChannelOfferTab(
        'channel-offer-pricing',
        formikProps,
        resellerAuthorization?.productType as PlanType | undefined
      ),
    },
    {
      key: 'channel-offer-other',
      label: 'Other',
      renderElement: (
        <div
          id={'channel-offer-other'}
          data-testid={'channel-offer-other-section'}
          className={classes.scrollTopMargin}
        >
          <ChannelOfferEula
            receivedResaleAuthEulaUrl={
              (resellerAuthorization?.endCustomerEulaFiles as string[])?.[0] ||
              ''
            }
          />
          <PrivateOfferStakeholders
            users={users}
            stakeholdersFaqs={
              <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.STAKEHOLDERS} />
            }
          />
          <PrivateOfferCustomMetaData />
          {resellerAuthorization?.resellerLicenseAgreementFiles && (
            <ResellerAgreementAcceptance
              resellerLicenseAgreementUrl={
                (
                  resellerAuthorization?.resellerLicenseAgreementFiles as string[]
                )?.[0]
              }
            />
          )}
        </div>
      ),
      isError: getIsErrorForChannelOfferTab(
        'channel-offer-other',
        formikProps,
        resellerAuthorization?.productType as PlanType | undefined
      ),
    },
  ]
  const { activeTabInex, scrollToSection } = scrollspyTabs({
    tabs: channelOfferTabs,
    offset,
  })

  return (
    <div className={classes.root}>
      <MainTabsV2
        activeTabIndex={activeTabInex}
        onChange={scrollToSection}
        tabs={channelOfferTabs}
        backGroundColor={theme.palette.textBackground.light}
        tabPosition="sticky"
        tabTop={'64px'}
      />
      {channelOfferTabs.map(
        channelOfferTabItem => channelOfferTabItem.renderElement
      )}
    </div>
  )
}
